/** Flag/local-name display metadata only — not the selectable list, use `useActiveLanguages()` for that. */
export const SUPPORTED_LANGUAGES = [
  { code: "ar", name: "Arabic",  localName: "العربية", flag: "🇸🇦" },
  { code: "es", name: "Spanish", localName: "Español",  flag: "🇪🇸" },
  { code: "fr", name: "French",  localName: "Français", flag: "🇫🇷" },
] as const;

export const PHOTOS_MAX_HERO = 5;

/** Soft rank only — nearby/pinned searches must still surface local hits. */
export const MAPBOX_MIN_SEARCH_RELEVANCE = 0.1;

/** API cap on room/category/floor/staff-role name fields — confirmed by a live 400 ("must be shorter than or equal to 255 characters"). */
export const ENTITY_NAME_MAX_LENGTH = 255;

/** API cap on postalCode specifically — confirmed by a live 400 ("must be shorter than or equal to 32 characters"), smaller than ENTITY_NAME_MAX_LENGTH. */
export const POSTAL_CODE_MAX_LENGTH = 32;

/** API cap on Location step's floor/aptUnit fields — confirmed by a live 400 ("must be shorter than or equal to 120 characters"). Distinct from ENTITY_NAME_MAX_LENGTH (that one's e.g. Basics' floor *name*, a different field on a different endpoint). */
export const LOCATION_FLOOR_UNIT_MAX_LENGTH = 120;

/** API cap on a custom scenic view's label — confirmed by a live 400 ("scenicViews.2.label must be shorter than or equal to 120 characters"). Smaller than ENTITY_NAME_MAX_LENGTH. */
export const SCENIC_VIEW_LABEL_MAX_LENGTH = 120;

/** API cap on a floor detail's name — confirmed by a live 400 ("floors.0.details.0.name must be shorter than or equal to 120 characters"). Distinct from the floor's own name (still ENTITY_NAME_MAX_LENGTH). */
export const FLOOR_DETAIL_NAME_MAX_LENGTH = 120;

/** API cap on a nearby place's placeType — confirmed by a live 400 ("must be shorter than or equal to 128 characters"). */
export const NEARBY_PLACE_TYPE_MAX_LENGTH = 128;

/** API cap on a nearby place's distance field — confirmed by a live 400 ("must be shorter than or equal to 64 characters"). */
export const NEARBY_PLACE_DISTANCE_MAX_LENGTH = 64;

/** API cap on a contact phone (E.164 string) — confirmed by a live 400 ("must be shorter than or equal to 32 characters"). Shared by every PhoneNumberField (Host, Supplier — same DTO shape). */
export const PHONE_MAX_LENGTH = 32;

export const DEFAULT_PROPERTY_PHOTO_CATEGORIES = [
  { id: "exterior", label: "Exterior", required: true },
  { id: "interior", label: "Interior", required: false },
  { id: "pool", label: "Pool & Outdoor", required: false },
] as const;

/** Proxied via next.config.ts's rewrite, same as TAXONOMY_ICON_CDN_PATH. */
export const PROPERTY_TYPE_IMAGES_CDN_PATH = "/images/catalogue/property-types";

/** Static property-type config — labels, icons, descriptions used across the wizard and dashboard. */
export const PROPERTY_TYPES = [
  {
    type: "villa",
    label: "Villa",
    description: "Luxury standalone residences with gardens, pools, and private amenities.",
    imageUrl: `${PROPERTY_TYPE_IMAGES_CDN_PATH}/villa.jpg`,
    icon: "home",
  },
  {
    type: "chalet",
    label: "Chalet",
    description: "Alpine masterpieces with ski access, spa, and mountain views.",
    imageUrl: `${PROPERTY_TYPE_IMAGES_CDN_PATH}/chalet.jpg`,
    icon: "mountain",
  },
  {
    type: "apartment",
    label: "Apartment",
    description: "Elegant city residences with panoramic views and premium finishes.",
    imageUrl: `${PROPERTY_TYPE_IMAGES_CDN_PATH}/apartment.jpg`,
    icon: "building",
  },
  {
    type: "island",
    label: "Island",
    description: "Exclusive waterfront or beachfront island sanctuaries with full service.",
    imageUrl: `${PROPERTY_TYPE_IMAGES_CDN_PATH}/island.jpg`,
    icon: "anchor",
  },
  {
    type: "castle",
    label: "Castle",
    description: "Historic estates with grounds, chapel, vineyard, and event facilities.",
    imageUrl: `${PROPERTY_TYPE_IMAGES_CDN_PATH}/castle.jpg`,
    icon: "castle",
  },
] as const;

/** Static dashboard hero background — no CMS backing, intentional design asset. */
export const DASHBOARD_HERO_IMAGE = `${PROPERTY_TYPE_IMAGES_CDN_PATH}/dashboard_hero.jpg`;

/** One SVG per taxonomy key — `feature_icons` and `amenity_items` share this folder — proxied via next.config.ts's rewrite. */
export const TAXONOMY_ICON_CDN_PATH = "/images/icons/amenities";

// ── Video upload ───────────────────────────────────────────────────────────────

/** Number of Azure blocks to PUT in parallel per batch. Azure recommends 4 for optimal throughput. */
export const CONCURRENT_BLOCK_UPLOADS = 4;

/** Maximum number of SAS refresh attempts before aborting a failed upload. */
export const MAX_SAS_REFRESHES = 3;

/** Maximum per-block retry attempts on transient failures (not SAS expiry) before surfacing the error. */
export const BLOCK_UPLOAD_MAX_RETRIES = 3;

/** Base delay in ms for exponential back-off between block retries. Doubles each attempt: 500ms, 1s, 2s. */
export const BLOCK_UPLOAD_RETRY_BASE_MS = 500;

/** How long a fetched supplier-details popover stays cached before refetching on reopen — contact info rarely changes mid-session. */
export const SUPPLIER_DETAILS_STALE_TIME_MS = 5 * 60_000;

/** CdnIcon names for supplier contact fields — shared by the Supplier wizard step and the listing table's supplier popover so both stay in sync. */
export const SUPPLIER_CONTACT_ICONS = {
  email: "mail-send-envelope--envelope-email-message-unopened-sealed-close--Streamline-Core",
  phone: "phone-telephone--android-phone-mobile-device-smartphone-iphone--Streamline-Core",
  notes: "interface-file-sticky-note--empty-common-file--Streamline-Core",
} as const;

/**
 * The name the catalogue API auto-assigns to amenity categories when no name
 * is submitted. Used to filter out truly untitled categories from the photos step.
 */
export const API_UNTITLED_CATEGORY = "Untitled Category";

/**
 * Fallback block size used when the upload session does not return a chunkSize.
 * Matches the server default (8 MB) so behaviour is consistent either way.
 */
export const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB

/**
 * Accepted video MIME types and max size for POST /media/upload-sessions —
 * matches the API's own contentType union and size limit exactly. Client-side
 * validation is just fast feedback; the server is the source of truth and
 * 400s outside these bounds regardless. Keep in sync with backend contract.
 */
export const ACCEPTED_VIDEO_TYPES = "video/mp4,video/quicktime,video/webm";
export const VIDEO_MAX_BYTES = 2 * 1024 * 1024 * 1024; // 2 GiB
export const VIDEO_FORMAT_HINT = "MP4, MOV, WebM — max 2GB";
// Derived from VIDEO_MAX_BYTES rather than a separate hardcoded "2GB" string,
// so the too-large error message can't silently drift out of sync with the
// actual limit the next time it changes.
export const VIDEO_TOO_LARGE_MESSAGE = `File too large. Maximum size is ${VIDEO_MAX_BYTES / (1024 * 1024 * 1024)}GB.`;

/**
 * `showOpenFilePicker` type filter (File System Access API — Chrome/Edge only).
 * Mirrors ACCEPTED_VIDEO_TYPES but in the {mimeType: extensions[]} shape that
 * API requires instead of a comma-joined string.
 */
export const VIDEO_PICKER_TYPES: FilePickerAcceptType[] = [
  {
    description: "Video",
    accept: {
      "video/mp4": [".mp4"],
      "video/quicktime": [".mov"],
      "video/webm": [".webm"],
    },
  },
];

/**
 * Accepted image MIME types and max size for POST /media/upload — matches
 * the API's documented "jpeg, png, webp or gif, max 20MB each".
 */
export const ACCEPTED_PHOTO_TYPES = "image/jpeg,image/png,image/webp,image/gif";
export const PHOTO_MAX_BYTES = 20 * 1024 * 1024; // 20 MB

/** Allowlist for the /api/wizard/media/fetch-blob proxy — never fetch an arbitrary host. */
export const BLOB_STORAGE_HOSTNAME_SUFFIX = ".blob.core.windows.net";

/**
 * The 5 house_rules taxonomy terms (by key) that PATCH /house-rules requires
 * an explicit true/false answer for before the wizard step can be submitted
 * — confirmed from the endpoint's own docs: "Submit requires an explicit
 * true or false for pets, events, smoking, quiet hours and commercial
 * filming — absence is not a no." These are also, not coincidentally, the
 * first 5 terms by sortOrder in the real house_rules catalog.
 */
export const REQUIRED_HOUSE_RULE_KEYS = [
  "pets_allowed",
  "events_allowed",
  "smoking_vaping_allowed",
  "quiet_hours",
  "commercial_filming_allowed",
] as const;

/** Client-side cap for the free-text "Additional rules" field on the Rules step. */
export const RULES_ADDITIONAL_RULES_MAX_LENGTH = 500;

/** Heuristic, not a documented API field: every custom taxonomy term observed live lands at this sortOrder, since POST /taxonomy/terms never lets the client set one, while seeded terms are always sequential from 0. */
export const CUSTOM_TERM_SORT_ORDER_SENTINEL = 9999;

/** API cap on a term's per-property note (and its translation, which shares this same limit) — confirmed live ("description translation must be at most 1000 characters"). Was 300 as an unconfirmed guess ("matching the demo app") before this. */
export const TERM_DESCRIPTION_MAX_LENGTH = 1000;

/** API cap on a term row's translated label — confirmed live ("label translation must be at most 120 characters"). Sent for every term regardless of curated status — see toValuePayload. */
export const TERM_LABEL_TRANSLATION_MAX_LENGTH = 120;

/** API cap on a cancellation policy's translated name/badge — no live-confirmed limit yet, matches TERM_LABEL_TRANSLATION_MAX_LENGTH's general cap for a short label. */
export const CANCELLATION_POLICY_LABEL_MAX_LENGTH = 120;

/** API cap on a cancellation policy's translated summary — confirmed from CustomCancellationPolicyDto.summary (maxLength: 500). */
export const CANCELLATION_POLICY_SUMMARY_MAX_LENGTH = 500;

/** Half-hour clock options (HH:mm value, 12-hour label) for the Rules step's time selects — matches the demo app's dropdown instead of a native time input. */
export const HALF_HOUR_TIME_OPTIONS: { value: string; label: string }[] = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? 0 : 30;
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return { value: `${hh}:${mm}`, label: `${displayHour}:${mm} ${period}` };
});

// ── About step ───────────────────────────────────────────────────────────────
/** API cap on the property name — confirmed live: a 400 ("name must be shorter than or equal to 120 characters") rejects the previous 250 guess. */
export const PROPERTY_NAME_MAX_LENGTH = 120;
export const PROPERTY_TAGLINE_MAX_LENGTH = 120;
export const PROPERTY_DESCRIPTION_MAX_LENGTH = 1000;
export const PROPERTY_SUB_DESCRIPTION_MAX_LENGTH = 1000;
export const FEATURE_TEXT_MAX_LENGTH = 120;

// ── Estate step ──────────────────────────────────────────────────────────────
/** API cap on interiorRooms/exteriorFeatures — confirmed live: UpdateEstateDto's `maxLength: 1000` on both. */
export const ESTATE_TEXTAREA_MAX_LENGTH = 1000;
/** API cap on a custom field's heading — confirmed live: CustomFieldDto.label `maxLength: 120`. */
export const CUSTOM_FIELD_LABEL_MAX_LENGTH = 120;
/** API cap on a custom field's description — confirmed live: CustomFieldDto.content `maxLength: 1000`. */
export const CUSTOM_FIELD_CONTENT_MAX_LENGTH = 1000;

// ── Host step ────────────────────────────────────────────────────────────────
export const HOST_NAME_MAX_LENGTH = 80;
export const HOST_LOCATION_MAX_LENGTH = 80;
export const HOST_BIO_MAX_LENGTH = 500;

export const SUPPLIER_NOTES_MAX_LENGTH = 500;
/** Matches UpdateHostDto.hostingSince min/max — max tracks the current year so it never goes stale. */
export const HOSTING_SINCE_MIN_YEAR = 1;
export const HOSTING_SINCE_MAX_YEAR = new Date().getFullYear();

// ── Pricing step ─────────────────────────────────────────────────────────────
export const DEFAULT_PRICING_CURRENCY = "USD";
/** Matches SetSellingPriceDto.rateType enum. */
export const PRICING_RATE_TYPE_OPTIONS: { value: import("@/types").PricingRateType; label: string }[] = [
  { value: "per_night", label: "Per Night" },
  { value: "per_week", label: "Per Week" },
  { value: "per_year", label: "Per Year" },
  { value: "total_nights", label: "Total Nights" },
];

/**
 * Client-side cap for the Pricing step's "Nights" field (total_nights rate).le.
 */
export const PRICING_MAX_NIGHTS = 99999;

/** Heading above the disabled-reasons banner on every step that can disable Next. */
export const FINISH_BEFORE_NEXT_HEADING = "Finish these steps before clicking Next:";

// ── Stale chunk recovery ─────────────────────────────────────────────────────
export const STALE_CHUNK_RELOAD_FLAG_KEY = "stale-chunk-reload-once";
/** How long the reload-once guard holds before clearing, so a later deploy can still trigger a reload. */
export const STALE_CHUNK_GUARD_RESET_MS = 10_000;

// ── Wizard forced read-only (listing "Preview" / Eye icon) ───────────────────
/** Query param the listing's Eye icon appends so the wizard opens read-only even for the owner — the API's `editable` reflects edit permission, not "this click meant view-only". */
export const WIZARD_VIEW_ONLY_PARAM = "view";
export const WIZARD_VIEW_ONLY_VALUE = "1";

// ── Route constants ──────────────────────────────────────────────────────────
/**
 * Every client-callable BFF path (`/api/...`) this app fetches, in one place
 * — mirrors the `APIS` constant pattern from westay-b2c-fe-web's
 * `src/constants/apis.ts`. These are always same-origin (Next.js's own file
 * routing resolves them), so there's no base URL to configure here — the
 * one upstream base URL (`NEXT_PUBLIC_CATALOGUE_API_URL`) already lives in
 * exactly one place, `catalogue-proxy.ts`. A path only has to match its
 * `src/app/api/...` route folder in one spot, not wherever it's called from.
 */

function property(vid: string): string {
  return `/api/wizard/properties/${vid}`;
}

function media(vid: string): string {
  return `${property(vid)}/media`;
}

export const ROUTES = {
  WIZARD: {
    PROPERTIES: "/api/wizard/properties",
    COUNTS: "/api/wizard/properties/counts",
    MEDIA_FETCH_BLOB: "/api/wizard/media/fetch-blob",
    PROPERTY: property,
    BASICS: (vid: string) => `${property(vid)}/basics`,
    ESTATE: (vid: string) => `${property(vid)}/estate`,
    ACCESSIBILITY: (vid: string) => `${property(vid)}/accessibility`,
    SAFETY: (vid: string) => `${property(vid)}/safety`,
    POLICIES: (vid: string) => `${property(vid)}/policies`,
    IMPORTANT_INFORMATION: (vid: string) => `${property(vid)}/important-information`,
    SUPPLIER: (vid: string) => `${property(vid)}/supplier`,
    RATING: (vid: string) => `${property(vid)}/rating`,
    ABOUT: (vid: string) => `${property(vid)}/about`,
    LOCATION: (vid: string) => `${property(vid)}/location`,
    AMENITIES: (vid: string) => `${property(vid)}/amenities`,
    HOST: (vid: string) => `${property(vid)}/host`,
    PRICING: (vid: string) => `${property(vid)}/pricing`,
    SELLING_PRICE: (propertyId: string) => `${property(propertyId)}/selling-price`,
    SUBMIT: (vid: string) => `${property(vid)}/submit`,
    APPROVE: (vid: string) => `${property(vid)}/approve`,
    REJECT: (vid: string) => `${property(vid)}/reject`,
    PUBLISH: (propertyId: string) => `${property(propertyId)}/publish`,
    UNPUBLISH: (propertyId: string) => `${property(propertyId)}/unpublish`,
    VERSIONS: (propertyId: string) => `${property(propertyId)}/versions`,
    OWNER: (propertyId: string) => `${property(propertyId)}/owner`,
    PREVIEW_TOKENS: (propertyId: string) => `${property(propertyId)}/preview-tokens`,
    PHOTOS: (vid: string) => `${property(vid)}/photos`,
    MEDIA: media,
    MEDIA_UPLOAD: (vid: string) => `${media(vid)}/upload`,
    MEDIA_ITEM: (vid: string, mediaId: string) => `${media(vid)}/${mediaId}`,
    MEDIA_CATEGORY: (vid: string, categoryId: string) => `${media(vid)}/categories/${categoryId}`,
    VIDEO_UPLOAD_SESSIONS: (vid: string) => `${media(vid)}/upload-sessions`,
    VIDEO_UPLOAD_SESSION: (vid: string, sessionId: string) => `${media(vid)}/upload-sessions/${sessionId}`,
    VIDEO_UPLOAD_SESSION_BLOCKS: (vid: string, sessionId: string) => `${media(vid)}/upload-sessions/${sessionId}/blocks`,
    VIDEO_UPLOAD_SESSION_COMPLETE: (vid: string, sessionId: string) => `${media(vid)}/upload-sessions/${sessionId}/complete`,
  },
  TAXONOMY: {
    TERMS: "/api/taxonomy/terms",
    LOCALES: "/api/taxonomy/locales",
  },
  PROPOSALS: {
    LIST: "/api/proposals",
    CUSTOMERS: "/api/proposals/customers",
    CUSTOMER_HISTORY: (norm: string) => `/api/proposals/customers/${encodeURIComponent(norm)}/history`,
    DETAIL: (id: string) => `/api/proposals/${id}`,
    SHARE_METHOD: (id: string) => `/api/proposals/${id}/share-method`,
    NOTES: (id: string) => `/api/proposals/${id}/notes`,
  },
  CANCELLATION_POLICIES: "/api/cancellation-policies",
  AUTH: {
    EXCHANGE_CODE: "/api/exchange-code",
    REFRESH: "/api/refresh",
    LOGOUT: "/api/logout",
    GATEWAY: "/api/gateway",
  },
} as const;
